import { useCallback, useMemo } from 'react';
import { Task, TasksListStorage } from '../../../types';

interface UseTaskNavigationParams {
  taskId: string | undefined;
  chainId: string | null;
  imageId: string | null;
}

interface UseTaskNavigationReturn {
  previousTaskUrl: string | null;
  nextTaskUrl: string | null;
  deactivateTaskInLocalStorage: (taskIdToUpdate: string) => void;
}

// Helper function to get tasks list from localStorage
const getTasksListFromStorage = (): TasksListStorage | null => {
  try {
    const storedData = localStorage.getItem('tasksList');
    if (!storedData) return null;
    return JSON.parse(storedData);
  } catch (error) {
    console.error('Error reading tasks from localStorage:', error);
    return null;
  }
};

// Helper function to save tasks list to localStorage
const saveTasksListToStorage = (tasksData: TasksListStorage): void => {
  try {
    localStorage.setItem('tasksList', JSON.stringify(tasksData));
  } catch (error) {
    console.error('Error saving tasks to localStorage:', error);
  }
};

export const useTaskNavigation = ({
  taskId,
  chainId,
  imageId,
}: UseTaskNavigationParams): UseTaskNavigationReturn => {
  const { previousTaskUrl, nextTaskUrl } = useMemo(() => {
    if (!taskId || !chainId) {
      return { previousTaskUrl: null, nextTaskUrl: null };
    }

    try {
      const parsed = getTasksListFromStorage();
      if (!parsed) {
        return { previousTaskUrl: null, nextTaskUrl: null };
      }

      // Check if chainId matches
      const currentChainId = parseInt(chainId, 10);

      if (parsed.chainId !== currentChainId) {
        return { previousTaskUrl: null, nextTaskUrl: null };
      }

      const tasks = parsed.tasks;
      const currentTaskId = parseInt(taskId, 10);
      const currentImageId = imageId ? parseInt(imageId, 10) : null;

      const validTasks = tasks.filter((task) => {
        const isCurrentTask = task.actionTaskId === currentTaskId;
        const hasDifferentImage = !currentImageId || task.imageId !== currentImageId;
        const isWorkable = task.isActive && !task.isWorked && !task.isAudited;

        return isCurrentTask || (isWorkable && hasDifferentImage);
      });

      // Find current task index in the filtered list
      const currentIndex = validTasks.findIndex((task) => task.actionTaskId === currentTaskId);

      if (currentIndex === -1) {
        return { previousTaskUrl: null, nextTaskUrl: null };
      }

      // Get previous and next tasks from filtered list
      const previousTask = currentIndex > 0 ? validTasks[currentIndex - 1] : null;
      const nextTask = currentIndex < validTasks.length - 1 ? validTasks[currentIndex + 1] : null;

      // Build URLs
      const buildTaskUrl = (task: Task) => {
        if (!task) return null;
        const params = new URLSearchParams();
        params.set('store_name', task.storeName);
        params.set('chain_id', task.chainId.toString());
        params.set('store_id', task.storeId.toString());
        params.set('cameraId', task.cameraId.toString());
        params.set('tag_name', task.tagName);
        params.set('image_id', task.imageId.toString());
        params.set('created_at', task.taskCreatedAt);
        params.set('osa_score', task.osaComplianceScore.toString());
        params.set('compliance_score', task.pogComplianceScore.toString());
        return `/tasks/${task.actionTaskId}?${params.toString()}`;
      };

      return {
        previousTaskUrl: previousTask ? buildTaskUrl(previousTask) : null,
        nextTaskUrl: nextTask ? buildTaskUrl(nextTask) : null,
      };
    } catch (error) {
      console.error('Error parsing tasks from localStorage:', error);
      return { previousTaskUrl: null, nextTaskUrl: null };
    }
  }, [taskId, chainId, imageId]);

  const deactivateTaskInLocalStorage = useCallback((taskIdToUpdate: string) => {
    const parsed = getTasksListFromStorage();
    if (!parsed) return;

    const taskIdNum = parseInt(taskIdToUpdate, 10);

    // Find and update the task
    const updatedTasks = parsed.tasks.map((task) => {
      if (task.actionTaskId === taskIdNum) {
        return { ...task, isActive: false };
      }
      return task;
    });

    // Save back to localStorage
    saveTasksListToStorage({ ...parsed, tasks: updatedTasks });
  }, []);

  return { previousTaskUrl, nextTaskUrl, deactivateTaskInLocalStorage };
};
